import React, { useEffect, useRef, useState } from 'react';
import { TextField, InputAdornment, IconButton, Typography, Box } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ShowWhen from '../Others/ShowWhen';
import { useAppTheme } from '../../theme/ThemeProvider/AppTheme';

const RoundedTextField = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  error = false,
  helperText,
  startIcon = null,
  endIcon = null,
  showPassword,
  onTogglePassword,
  multiline = false,
  rows,
  required = false,
  disabled = false,
  sx = {},
  containerSx = {},
  validateValue = (_) => '',
  validateValueRefreshDependency = [],
  onFocus,
  onBlur,
  ...props
}) => {
  const { getColor } = useAppTheme();

  const isPassword = type === 'password';

  const [isError, setError] = useState(false);
  const [isFocus, setIsFocus] = useState(false);
  const needToValidate = useRef([null, undefined, ''].includes(value) ? false : true);

  const hasError = !disabled && !!(error || isError);

  function handleOnChange(event) {
    const { value } = event.target;
    onChange?.(event, value);
  }

  function validateError() {
    if (!needToValidate.current) {
      needToValidate.current = true;
      return;
    }

    setError(validateValue(value));
  }

  function handlerOnFocus(event) {
    setIsFocus(true);
    onFocus?.(event);
  }

  function handleOnBlur(event) {
    needToValidate.current = true;
    validateError();
    setIsFocus(false);
    onBlur?.(event);
  }

  useEffect(() => {
    validateError();
  }, [value, ...validateValueRefreshDependency]);

  return (
    <Box sx={{ width: '100%', ...containerSx }}>
      <TextField
        label={label}
        type={isPassword && showPassword ? 'text' : type}
        value={value}
        onChange={handleOnChange}
        placeholder={placeholder}
        error={hasError}
        helperText={helperText}
        required={required}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        fullWidth
        InputProps={{
          startAdornment: startIcon && (
            <InputAdornment position="start">
              {startIcon}
            </InputAdornment>
          ),
          endAdornment: isPassword ? (
            <InputAdornment position="end">
              <IconButton
                onClick={onTogglePassword}
                edge="end"
                sx={{
                  color: 'var(--color-text-secondary)',
                  '&:hover': {
                    color: 'var(--color-primary)',
                    bgcolor: 'var(--color-bg-secondary)',
                  },
                }}
              >
                {showPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </IconButton>
            </InputAdornment>
          ) : endIcon ? (
            <InputAdornment position="end">{endIcon}</InputAdornment>
          ) : null,
        }}
        sx={{
          borderRadius: '12px',
          p: 0,
          '& .MuiInputBase-root': {
            overflow: 'hidden',
          },
          '& .MuiInputLabel-root': {
            color: 'var(--color-text-secondary)',
            fontSize: '14px',
            fontWeight: 500,
            borderRadius: '4px',
            '&.Mui-focused': {
              color: hasError ? 'var(--color-error)' : 'var(--color-primary)',
              backgroundColor: 'var(--color-action-focus)',
              padding: '0 8px',
            },
            '&.Mui-error': {
              color: 'var(--color-error)',
            },
            '&.MuiInputLabel-shrink': {
              backgroundColor: 'var(--color-bg)',
              padding: '0 8px',
              borderRadius: '4px',
            },
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: hasError ? 'var(--color-error-light)' : 'var(--color-bg)',
            border: hasError ? '1px solid #fca5a5' : `1px solid ${getColor('text', 0.2)}`,
            fontSize: '14px',
            fontWeight: 400,
            transition: 'all 0.2s ease',
            padding: 0,
            '& fieldset': {
              border: 'none',
            },
            '&:hover': {
              bgcolor: hasError ? 'var(--color-error-light)' : getColor('bg', 0.95),
              borderColor: hasError ? 'var(--color-error)' : getColor('primary', 0.2),
            },
            '&.Mui-focused': {
              bgcolor: 'var(--color-bg)',
              borderColor: hasError ? 'var(--color-error)' : getColor('primary', 0.4),
              boxShadow: `0 0 0 3px ${getColor(hasError ? 'error' : 'primary', 0.1)}`,
            },
            '&.Mui-disabled': {
              bgcolor: 'var(--color-bg-default)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-muted)',
            },
          },
          '& .MuiInputBase-input': {
            color: hasError ? 'var(--color-error)' : 'var(--color-text)',
            fontSize: '14px',
            fontWeight: 400,
            py: 1.5,
            fontFamily: 'inherit',
            '&::placeholder': {
              color: hasError ? 'var(--color-error)' : 'var(--color-text-muted)',
              opacity: 1,
            },
            '&.Mui-disabled': {
              color: '#94a3b8',
              WebkitTextFillColor: 'var(--color-text-muted)',
            },
          },
          '& .MuiFormHelperText-root': {
            fontSize: '12px',
            mt: 1,
            color: hasError ? 'var(--color-error)' : 'var(--color-text-muted)',
          },
          '&:hover': {
            bgcolor: hasError ? 'var(--color-error-light)' : getColor('bg', 0.95),
          },
          '& .MuiInputAdornment-root': {
            height: 'auto',
            display: 'flex',
            alignItems: 'center',
            ml: startIcon ? '12px' : 0,
            color: 'var(--color-text-secondary)',
            '& > *': { 
                mr: 0 
            },
          },
          ...sx,
        }}
        onFocus={handlerOnFocus}
        onBlur={handleOnBlur}
        {...props}
      />

      <ShowWhen when={!disabled && !!isError && !isFocus}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '12px',
            color: 'var(--color-error)',
            paddingLeft: '12px',
            WebkitLineClamp: 1,
            lineClamp: 1,
            overflow: 'hidden',
          }}
        >
          {isError}
        </Typography>
      </ShowWhen>
    </Box>
  );
};

export default RoundedTextField;
